// Copyright (c) Ping. All rights reserved.
//
// Client-derived Activity feed (Figma 2804:106793 / activities.png).
//
// The Figma frame carries a "We still need to discuss how this will
// work in the backend. Don't recreate yet" note — so there is NO
// activity envelope in the wire protocol and `docs/architecture` has
// no activity push contract (confirmed). Until the BE lands one we
// DERIVE the feed locally by projecting the message/reaction state we
// already persist in Dexie (`apps/web/src/core/persistence/db.ts`)
// into the shared `ActivityItemRecord` shape that `<ActivityFeedScreen>`
// (packages/ui) renders. Three sources, all "things that happened TO
// me":
//
//   1. mention  — an incoming message whose `mentions[]` carries my
//                 accountId (projected by the shared incoming hook into
//                 `Message.mentions`, see packages/ui/src/sync/
//                 useIncomingMessages.ts §"@mentions").
//   2. reaction — a `reactions` row whose target message I authored,
//                 reacted to by someone other than me.
//   3. dm (reply)— an incoming message whose `inReplyTo` parent I
//                 authored (a threaded reply back to me).
//
// "Authored by me" === `Message.senderAccountId === getCurrentUser-
// AccountId()` — self-sent rows store the bare accountId while peer
// rows store the device-hex (so a peer message can never be mistaken
// for self). This mirrors the `isSelf` check in ChatRouteHost.
//
// The module exposes a `load`/`subscribe` pair matching the
// `ActivityLoader` / `ActivitySubscriber` contract the screen owns,
// plus a documented `subscribeActivityPush` BE seam (currently a
// no-op) so a future server push can override the derived feed without
// touching the screen.

import { liveQuery } from 'dexie';

import type {
  ActivityCta,
  ActivityItemRecord,
  ActivityKind,
  ActivityLead,
  ActivityLoader,
  ActivityRowKind,
  ActivitySort,
  ActivitySubscriber,
} from '@ping/ui';
// Deep import (not the `@ping/ui` / appEvent barrels — they break vitest's
// import analysis) of the same decoder the chat list uses, so an activity
// preview reads "seen @Agboba Gideon ?" / "📷 Photo · caption" instead of the
// raw `{kind,payload}` envelope JSON stored in `Message.body`.
import { formatMessagePreview } from '@ping/ui/src/appEvent/formatPreview';
// The canonical game taxonomy (type-only, pure types → safe under vitest) so a
// game row can carry its game kind and render that game's own icon.
import type { GameKind } from '@ping/ui/src/appEvent/types';
// Deep import (not the `@ping/ui` barrel) so this module — and its pure
// `deriveActivityItems` export — can be imported under vitest without pulling
// in the RN render harness / toast deps that break vite's import analysis
// (see MEMORY: ui-vitest-infra-gotchas).
import {
  getCurrentUserAccountId,
  getCurrentUserDisplayName,
  subscribeCurrentUserAccountId,
} from '@ping/ui/src/sync/registerCurrentUser';

import {
  type CallHistoryEntry,
  type Conversation,
  db,
  type Message,
  type PollVote,
  type Reaction,
  type ReadPointer,
  type Reminder,
} from '@core/persistence/db';

// ---------------------------------------------------------------------------
// BE SEAM
// ---------------------------------------------------------------------------
// A future backend will push an authoritative activity feed (the Figma
// note: "still need to discuss how this will work in the backend").
// When that lands, wire the server stream here and return an
// unsubscribe; `subscribeActivityFeed` below already prefers a push
// source over the local derivation when one is registered. Until then
// this is a documented no-op so the call site stays stable.
//
// PROPOSED CONTRACT (mirrors apps/desktop/src/network/activity.ts):
//   WS frame  { type: "activity", items: ActivityItemWire[] }
//   GET /v1/activity?filter=<ActivityKind>&sort=<ActivitySort>
// `ActivityItemWire` is structurally identical to `ActivityItemRecord`.
export type ActivityPushSource = (
  filter: ActivityKind,
  sort: ActivitySort,
  push: (items: readonly ActivityItemRecord[]) => void,
) => () => void;

let activityPushSource: ActivityPushSource | null = null;

/**
 * Register a backend push source for the activity feed. Returns a
 * disposer that unregisters it. When a source is registered the derived
 * feed below is bypassed and the screen renders the pushed items
 * verbatim. This is the single integration point a future BE slice
 * needs — no screen or view-model change required.
 */
export function registerActivityPushSource(
  source: ActivityPushSource,
): () => void {
  activityPushSource = source;
  return () => {
    if (activityPushSource === source) activityPushSource = null;
  };
}

// ---------------------------------------------------------------------------
// Derivation
// ---------------------------------------------------------------------------

const PREVIEW_MAX = 120;

function truncate(text: string): string {
  const t = text.trim();
  return t.length > PREVIEW_MAX ? `${t.slice(0, PREVIEW_MAX - 1)}…` : t;
}

/** Decode a stored `Message.body` (plain text OR a `{kind,payload}` envelope)
 * into the human-readable preview the chat list shows — inlining @mentions and
 * adding the kind glyph for media/calls/polls/etc. Falls back to the raw
 * (truncated) body when it isn't decodable. */
function previewOf(body: string | undefined, fallback: string): string {
  if (body === undefined || body.length === 0) return fallback;
  const decoded = formatMessagePreview(body);
  if (decoded !== undefined && decoded.length > 0) return decoded;
  // A structured `{ kind, payload }` envelope that the formatter can't turn
  // into a preview (a marker/sidecar kind like `com.ping.thread_root`, or a
  // receipt) must NEVER leak its raw JSON into the feed — fall back to the
  // caller's human label. Only a genuine plain-text body flows through to the
  // (truncated) body itself.
  if (body.charAt(0) === '{') return fallback;
  return truncate(body);
}

/** The meeting title off a `com.ping.meeting` create body (for the RSVP row's
 *  emphasised tail), or undefined when unresolvable. */
function titleOfMeeting(body: string | undefined): string | undefined {
  const env = peekEnvelope(body);
  if (env?.kind !== 'com.ping.meeting') return undefined;
  const title = asString(env.payload.title);
  return title !== undefined && title.trim().length > 0 ? truncate(title) : undefined;
}

/** Human RSVP preview from a `com.ping.meeting_rsvp` `response` field. */
function rsvpResponsePreview(response: unknown): string {
  switch (response) {
    case 'yes':
      return 'Going';
    case 'maybe':
      return 'Maybe';
    case 'no':
      return 'Not going';
    default:
      return 'Responded to your meeting';
  }
}

/** Display name for an account/device id within a conversation. */
function nameFor(
  conv: Conversation | undefined,
  id: string | undefined,
): string | null {
  if (id === undefined || id.length === 0) return null;
  if (conv === undefined) return null;
  // Direct accountId / device-hex hit.
  const direct = conv.memberDisplayNames?.[id] ?? conv.memberDeviceNames?.[id];
  if (direct !== undefined && direct.length > 0) return direct;
  // `id` is a device-hex → resolve to its accountId → display name.
  const acct = conv.memberDeviceAccounts?.[id];
  if (acct !== undefined) {
    const viaAcct = conv.memberDisplayNames?.[acct];
    if (viaAcct !== undefined && viaAcct.length > 0) return viaAcct;
  }
  return null;
}

/** Resolved avatar for an account/device `id` within a conversation, for the
 *  row's actor picture (and game lead). Groups: the accountId-keyed member
 *  roster URL. 1:1: the conversation avatar IS the peer's picture — prefer the
 *  live presigned `avatarUri`, else the stable `avatarMediaId` (Avatar resolves
 *  a bare media id to a presigned URL itself). Returns undefined when we have no
 *  picture, so the row degrades cleanly to initials. */
function avatarFor(
  conv: Conversation | undefined,
  id: string | undefined,
): string | undefined {
  if (conv === undefined || id === undefined || id.length === 0) return undefined;
  // `id` may be a device-hex (message sender) or already an accountId; the
  // roster maps are accountId-keyed, so resolve the hex first (a no-op for an
  // accountId, which isn't a key in the device→account map).
  const accountId = conv.memberDeviceAccounts?.[id] ?? id;
  const roster = conv.memberAvatarUris?.[accountId];
  if (roster !== undefined && roster.length > 0) return roster;
  if (conv.kind === 'direct') {
    if (conv.avatarUri !== undefined && conv.avatarUri.length > 0) return conv.avatarUri;
    if (conv.avatarMediaId !== undefined && conv.avatarMediaId.length > 0)
      return conv.avatarMediaId;
  }
  return undefined;
}

/** A Message row may carry an extra `mentions: string[]` field written
 * by the shared incoming hook — it is not declared on the web `Message`
 * interface, so read it through a narrow structural view. */
function messageMentions(m: Message): readonly string[] {
  const raw = (m as { mentions?: unknown }).mentions;
  if (!Array.isArray(raw)) return [];
  return raw.filter((x): x is string => typeof x === 'string');
}

/** Display names @-mentioned in this message (the reliable self-mention signal
 * in a 1:1, where the mention `user_id` is a per-side conversation id). */
function messageMentionNames(m: Message): readonly string[] {
  const raw = (m as { mentionNames?: unknown }).mentionNames;
  if (!Array.isArray(raw)) return [];
  return raw.filter((x): x is string => typeof x === 'string');
}

/** True when this message @-mentions the local user — by account id (groups)
 * OR by display name (1:1, where the mention id isn't an account id). */
function mentionsSelf(
  m: Message,
  selfAccountId: string,
  selfDisplayName: string | null,
): boolean {
  if (messageMentions(m).includes(selfAccountId)) return true;
  if (selfDisplayName !== null && selfDisplayName.length > 0) {
    const lower = selfDisplayName.toLowerCase();
    return messageMentionNames(m).some((n) => n.toLowerCase() === lower);
  }
  return false;
}

/** Inputs to the derivation — all the local state the feed projects from. */
export interface DeriveInputs {
  readonly messages: readonly Message[];
  readonly reactions: readonly Reaction[];
  readonly conversations: readonly Conversation[];
  readonly pollVotes: readonly PollVote[];
  readonly callHistory: readonly CallHistoryEntry[];
  readonly reminders: readonly Reminder[];
  readonly readPointers: readonly ReadPointer[];
  /** Activity ids the user "erased" — filtered out (re-derivation would
   *  otherwise resurface them). */
  readonly dismissedIds: ReadonlySet<string>;
  /** Activity ids the user explicitly marked unread (per-item override on top
   *  of the conversation read pointer, which can only advance). */
  readonly forcedUnreadIds: ReadonlySet<string>;
}

/** Lightweight `{ kind, payload }` peek at a message's stored envelope body
 *  (game rows persist the full JSON envelope). Returns null for plain text. */
function peekEnvelope(
  body: string | undefined,
): { kind: string; payload: Record<string, unknown> } | null {
  if (body === undefined || body.length === 0 || body[0] !== '{') return null;
  try {
    const parsed = JSON.parse(body) as { kind?: unknown; payload?: unknown };
    if (typeof parsed.kind !== 'string') return null;
    return {
      kind: parsed.kind,
      payload:
        parsed.payload !== null && typeof parsed.payload === 'object'
          ? (parsed.payload as Record<string, unknown>)
          : {},
    };
  } catch {
    return null;
  }
}

const GAME_KINDS: ReadonlySet<GameKind> = new Set<GameKind>([
  'coinflip',
  'dice',
  'rps',
  'chess',
  'blackjack',
  'racing',
]);

/** Narrow an envelope's `game_kind` field to the known game taxonomy. */
function asGameKind(value: unknown): GameKind | undefined {
  return typeof value === 'string' && GAME_KINDS.has(value as GameKind)
    ? (value as GameKind)
    : undefined;
}

/** String value of an envelope field, or undefined. */
function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/**
 * Project the persisted local state into activity rows. Pure over its
 * inputs so it is trivially unit-testable and reused by both `load`
 * (one-shot read) and `subscribe` (liveQuery re-projection). Sources:
 * mentions, reactions, replies/threads, game challenges/outcomes, poll
 * votes, and missed calls — all "things that happened TO me". Unread is
 * computed from the synced read pointers; pending reminders annotate the
 * matching row.
 */
export function deriveActivityItems(
  selfAccountId: string | null,
  inputs: DeriveInputs,
  selfDisplayName: string | null = null,
): ActivityItemRecord[] {
  if (selfAccountId === null) return [];
  const { messages, reactions, conversations, pollVotes, callHistory, reminders, readPointers, dismissedIds, forcedUnreadIds } =
    inputs;

  const convById = new Map<string, Conversation>();
  for (const c of conversations) convById.set(c.id, c);

  // Read pointer per conversation → unread = activity newer than last read.
  const readAtByConv = new Map<string, number>();
  for (const rp of readPointers) readAtByConv.set(rp.conversationId, rp.lastReadAt);
  const isUnread = (conversationId: string, ts: number): boolean => {
    const readAt = readAtByConv.get(conversationId);
    return readAt === undefined || ts > readAt;
  };

  // Pending reminders keyed by the message they anchor to (+ a conversation
  // fallback) so a row can show its reminder.
  const reminderByMessage = new Map<string, number>();
  const reminderByConv = new Map<string, number>();
  for (const rm of reminders) {
    if (rm.messageId !== undefined) reminderByMessage.set(rm.messageId, rm.fireAt);
    else reminderByConv.set(rm.conversationId, rm.fireAt);
  }
  const reminderFor = (conversationId: string, messageId: string): number | undefined =>
    reminderByMessage.get(messageId) ?? reminderByConv.get(conversationId);

  // Index my own messages by content hash + id so reaction/reply
  // projection can resolve "is this mine" + render a preview.
  const myByHash = new Map<string, Message>();
  const myById = new Map<string, Message>();
  for (const m of messages) {
    if (m.deletedAt !== undefined) continue;
    if (m.senderAccountId === selfAccountId) {
      myByHash.set(m.contentHash, m);
      myById.set(m.id, m);
    }
  }
  const pollMineByHash = new Map<string, Message>();
  // meeting_id → my own meeting-create message, so an RSVP from another member
  // to a meeting *I scheduled* surfaces as "Responded to your meeting" (the
  // meeting analogue of the poll-vote row). Keyed by the wire meeting_id since
  // that's what the RSVP sidecar carries (not a content hash).
  const myMeetingById = new Map<string, Message>();
  for (const m of messages) {
    if (m.deletedAt !== undefined) continue;
    if (m.senderAccountId !== selfAccountId) continue;
    const env = peekEnvelope(m.body);
    if (env?.kind === 'com.ping.poll') pollMineByHash.set(m.contentHash, m);
    if (env?.kind === 'com.ping.meeting') {
      const meetingId = asString(env.payload.meeting_id);
      if (meetingId !== undefined) myMeetingById.set(meetingId, m);
    }
  }

  // session_id → game kind, from every game-create envelope (either side), so an
  // OUTCOME row — whose envelope carries only the session id — can still render
  // that game's icon.
  const gameKindBySession = new Map<string, GameKind>();
  for (const m of messages) {
    if (m.deletedAt !== undefined) continue;
    const env = peekEnvelope(m.body);
    if (env?.kind !== 'com.ping.game_create') continue;
    const kind = asGameKind(env.payload.game_kind);
    const session = asString(env.payload.session_id);
    if (kind !== undefined && session !== undefined) {
      gameKindBySession.set(session, kind);
    }
  }

  const items: ActivityItemRecord[] = [];

  for (const m of messages) {
    if (m.deletedAt !== undefined) continue;
    if (m.senderAccountId === selfAccountId) continue; // what happened TO me
    const conv = convById.get(m.conversationId);
    const actorName = nameFor(conv, m.senderAccountId);
    const actorAvatarUri = avatarFor(conv, m.senderAccountId);
    const env = peekEnvelope(m.body);

    // `com.ping.thread_root` is a system ANCHOR marker the sender emits once,
    // before the first thread reply — not a user action directed at me. It
    // carries only `{ from }` (an accountId, no text), yet it rides with
    // `parent_id` = my root message, so on receipt its reconstructed
    // `inReplyTo.messageId` points at a message I authored and it would
    // otherwise fall through to the reply branch below as a raw-JSON row. The
    // genuine reply (a sibling `com.ping.text` with `payload.thread`) already
    // produces the "replied to you / Thread in" row with a real preview, so
    // surface nothing for the marker. Mirrors the thread panel's guard
    // (ChatRouteHost.tsx / webThreadGateway.ts: skip `com.ping.thread_root`).
    if (env?.kind === 'com.ping.thread_root') continue;

    // Game challenge / outcome (envelope-detected; game state isn't in Dexie).
    // The lead visual is the game's own icon (chess crown, dice, …); we fall
    // back to the challenger's avatar only when the game kind can't be resolved.
    if (env?.kind === 'com.ping.game_create') {
      const kind = asGameKind(env.payload.game_kind);
      items.push(
        makeItem('game', m, actorName, isUnread, reminderFor, {
          lead:
            kind !== undefined
              ? { type: 'gameIcon', kind }
              : { type: 'avatar', uri: actorAvatarUri ?? null },
          actorAvatarUri,
          actionLabel: 'Challenged you!',
          cta: { kind: 'game_turn', label: 'Play your turn' },
          previewText: 'Challenged you to a game',
        }),
      );
      continue;
    }
    if (env?.kind === 'com.ping.game_outcome') {
      const session = asString(env.payload.session_id);
      const kind = session !== undefined ? gameKindBySession.get(session) : undefined;
      items.push(
        makeItem('game', m, actorName, isUnread, reminderFor, {
          lead:
            kind !== undefined
              ? { type: 'gameIcon', kind }
              : { type: 'avatar', uri: actorAvatarUri ?? null },
          actorAvatarUri,
          actionLabel: 'Accepted your challenge.',
          cta: { kind: 'game_result', label: 'View Result' },
          previewText: 'Game result is ready',
        }),
      );
      continue;
    }

    // Meeting scheduled by another member (§2.13). The whole group is invited,
    // so surface it like a game challenge: a calendar lead + a preview that
    // routes back to the card (where RSVP happens). The `📅 Meeting: <title>`
    // preview comes from `formatMessagePreview`.
    if (env?.kind === 'com.ping.meeting') {
      items.push(
        makeItem('meeting', m, actorName, isUnread, reminderFor, {
          lead: { type: 'icon', name: 'meeting' },
          actorAvatarUri,
          actionLabel: 'Invited you to a meeting.',
          previewText: previewOf(m.body, 'invited you to a meeting'),
        }),
      );
      continue;
    }
    // Someone RSVP'd to a meeting I scheduled — the meeting analogue of the
    // poll-vote row. Only my own meetings qualify (matched by wire meeting_id).
    if (env?.kind === 'com.ping.meeting_rsvp') {
      const meetingId = asString(env.payload.meeting_id);
      const mine = meetingId !== undefined ? myMeetingById.get(meetingId) : undefined;
      if (mine !== undefined) {
        const title = titleOfMeeting(mine.body);
        items.push(
          makeItem('meeting', m, actorName, isUnread, reminderFor, {
            lead: { type: 'icon', name: 'meeting' },
            actorAvatarUri,
            actionLabel: 'Responded to your meeting.',
            ...(title !== undefined ? { actionEmphasis: title } : {}),
            previewText: rsvpResponsePreview(env.payload.response),
          }),
        );
      }
      continue;
    }

    // Mention of me (by account id in groups, or display name in a 1:1).
    if (mentionsSelf(m, selfAccountId, selfDisplayName)) {
      items.push(
        makeItem('mention', m, actorName, isUnread, reminderFor, {
          lead: { type: 'icon', name: 'mention' },
          actorAvatarUri,
          actionLabel: 'Mentioned you in a message.',
          previewText: previewOf(m.body, 'mentioned you'),
        }),
      );
      continue;
    }

    // Reply / thread to a message I authored.
    if (m.inReplyTo !== undefined && myById.has(m.inReplyTo.messageId)) {
      const isGroup = conv?.kind === 'group';
      items.push(
        makeItem('reply', m, actorName, isUnread, reminderFor, {
          lead: { type: 'icon', name: 'thread' },
          actorAvatarUri,
          actionLabel: isGroup ? 'Thread in' : 'Replied to you.',
          actionEmphasis: isGroup ? (conv?.name ?? null) : null,
          previewText: previewOf(m.body, 'replied to you'),
        }),
      );
      continue;
    }
  }

  // Reactions to my messages, by someone other than me.
  for (const r of reactions) {
    if (r.actorAccountId === selfAccountId) continue;
    const target =
      (r.targetMessageId !== undefined ? myById.get(r.targetMessageId) : undefined) ??
      myByHash.get(r.targetContentHash);
    if (target === undefined) continue;
    const conv = convById.get(r.conversationId);
    const actorName = nameFor(conv, r.actorAccountId);
    const actorAvatarUri = avatarFor(conv, r.actorAccountId);
    const id = `reaction:${r.id}`;
    if (dismissedIds.has(id)) continue;
    const remAt = reminderFor(r.conversationId, target.id);
    items.push({
      id,
      kind: 'reaction',
      timestamp: r.addedAt,
      // Per Figma the reaction row's preview is the ORIGINAL message (the emoji
      // is the lead, the subtitle says "Reacted to your message.").
      previewText: previewOf(target.body, 'your message'),
      unread: isUnread(r.conversationId, r.addedAt),
      actorId: null,
      actorName,
      conversationId: (r.conversationId as ActivityItemRecord['conversationId']),
      lead: { type: 'emoji', value: r.emoji },
      actionLabel: 'Reacted to your message.',
      ...(actorAvatarUri !== undefined ? { actorAvatarUri } : {}),
      ...(remAt !== undefined ? { reminderAt: remAt } : {}),
      routePayload: JSON.stringify({
        conversationId: r.conversationId,
        messageId: target.id,
      }),
    });
  }

  // Poll votes on a poll I authored, by someone other than me.
  for (const v of pollVotes) {
    if (v.actorAccountId === selfAccountId) continue;
    const poll = pollMineByHash.get(v.pollTarget);
    if (poll === undefined) continue;
    const conv = convById.get(poll.conversationId);
    const actorName = nameFor(conv, v.actorAccountId);
    const actorAvatarUri = avatarFor(conv, v.actorAccountId);
    const id = `poll:${v.id}`;
    if (dismissedIds.has(id)) continue;
    items.push({
      id,
      kind: 'poll',
      timestamp: v.votedAt,
      previewText: 'Voted in your poll',
      unread: isUnread(poll.conversationId, v.votedAt),
      actorId: null,
      actorName,
      conversationId: (poll.conversationId as ActivityItemRecord['conversationId']),
      lead: { type: 'icon', name: 'poll' },
      actionLabel: 'Voted in your poll.',
      ...(actorAvatarUri !== undefined ? { actorAvatarUri } : {}),
      routePayload: JSON.stringify({
        conversationId: poll.conversationId,
        messageId: poll.id,
      }),
    });
  }

  // Missed incoming calls.
  for (const call of callHistory) {
    if (call.type !== 'missed') continue;
    const conv = convById.get(call.conversationId);
    const caller = call.participantAccountIds.find((a) => a !== selfAccountId);
    const actorName = nameFor(conv, caller) ?? conv?.name ?? null;
    const actorAvatarUri = avatarFor(conv, caller);
    const id = `call:${call.id}`;
    if (dismissedIds.has(id)) continue;
    items.push({
      id,
      kind: 'missedCall',
      timestamp: call.startedAt,
      previewText: call.isVideo ? 'Missed video call' : 'Missed call',
      unread: isUnread(call.conversationId, call.startedAt),
      actorId: null,
      actorName,
      conversationId: (call.conversationId as ActivityItemRecord['conversationId']),
      lead: { type: 'icon', name: 'missedCall' },
      actionLabel: call.isVideo ? 'Missed video call.' : 'Missed call.',
      ...(actorAvatarUri !== undefined ? { actorAvatarUri } : {}),
      routePayload: JSON.stringify({ conversationId: call.conversationId }),
    });
  }

  return items
    .filter((it) => !dismissedIds.has(it.id))
    .map((it) =>
      forcedUnreadIds.has(it.id) && !it.unread ? { ...it, unread: true } : it,
    );
}

/** Extra rich fields a source supplies for a message-backed row. */
interface RichFields {
  readonly lead?: ActivityLead;
  /** Actor's inline avatar (16×16 chip next to the name). */
  readonly actorAvatarUri?: string | undefined;
  readonly actionLabel?: string;
  readonly actionEmphasis?: string | null;
  readonly bodyText?: string;
  readonly bodyMentions?: readonly string[];
  readonly cta?: ActivityCta;
  readonly previewText: string;
}

function makeItem(
  kind: ActivityRowKind,
  m: Message,
  actorName: string | null,
  isUnread: (conversationId: string, ts: number) => boolean,
  reminderFor: (conversationId: string, messageId: string) => number | undefined,
  rich: RichFields,
): ActivityItemRecord {
  const reminderAt = reminderFor(m.conversationId, m.id);
  return {
    id: `${kind}:${m.id}`,
    kind,
    timestamp: m.sentAt,
    previewText: rich.previewText,
    unread: isUnread(m.conversationId, m.sentAt),
    actorId: null,
    actorName,
    conversationId: (m.conversationId as ActivityItemRecord['conversationId']),
    ...(rich.lead !== undefined ? { lead: rich.lead } : {}),
    ...(rich.actorAvatarUri !== undefined ? { actorAvatarUri: rich.actorAvatarUri } : {}),
    ...(rich.actionLabel !== undefined ? { actionLabel: rich.actionLabel } : {}),
    ...(rich.actionEmphasis !== undefined ? { actionEmphasis: rich.actionEmphasis } : {}),
    ...(rich.bodyText !== undefined ? { bodyText: rich.bodyText } : {}),
    ...(rich.bodyMentions !== undefined ? { bodyMentions: rich.bodyMentions } : {}),
    ...(rich.cta !== undefined ? { cta: rich.cta } : {}),
    ...(reminderAt !== undefined ? { reminderAt } : {}),
    routePayload: JSON.stringify({
      conversationId: m.conversationId,
      messageId: m.id,
    }),
  };
}

/** Apply the filter chip + sort mode the view-model passes down. */
export function applyFilterSort(
  items: readonly ActivityItemRecord[],
  filter: ActivityKind,
  sort: ActivitySort,
): ActivityItemRecord[] {
  const filtered =
    filter === 'all' ? [...items] : items.filter((i) => i.kind === filter);

  switch (sort) {
    case 'byPerson':
      filtered.sort(
        (a, b) =>
          (a.actorName ?? '').localeCompare(b.actorName ?? '') ||
          b.timestamp - a.timestamp,
      );
      break;
    case 'byChannel':
      filtered.sort(
        (a, b) =>
          (a.conversationId ?? '').localeCompare(b.conversationId ?? '') ||
          b.timestamp - a.timestamp,
      );
      break;
    case 'mostRecent':
    default:
      filtered.sort((a, b) => b.timestamp - a.timestamp);
      break;
  }
  return filtered;
}

// Local-only (per-device) activity overrides stored in `settings_kv` so they
// survive reloads + re-derivation. "Erase" hides a row; "Mark as unread" forces
// a row unread on top of the (advance-only) conversation read pointer.
const DISMISSED_KV_KEY = 'activity.dismissedIds';
const FORCED_UNREAD_KV_KEY = 'activity.forcedUnreadIds';

async function readIdSet(key: string): Promise<Set<string>> {
  try {
    const row = await db.settings_kv.get(key);
    const raw = (row as { value?: unknown } | undefined)?.value;
    if (Array.isArray(raw)) return new Set(raw.filter((x): x is string => typeof x === 'string'));
  } catch {
    /* best-effort */
  }
  return new Set();
}

async function writeIdSet(key: string, set: ReadonlySet<string>): Promise<void> {
  await db.settings_kv.put({ key, value: [...set], updatedAt: Date.now() });
}

/** Add an activity id to the local dismissed set (the "Erase" action). */
export async function dismissActivity(id: string): Promise<void> {
  const current = await readIdSet(DISMISSED_KV_KEY);
  current.add(id);
  await writeIdSet(DISMISSED_KV_KEY, current);
}

/** Force an activity row unread (per-item "Mark as unread"). */
export async function forceActivityUnread(id: string): Promise<void> {
  const current = await readIdSet(FORCED_UNREAD_KV_KEY);
  current.add(id);
  await writeIdSet(FORCED_UNREAD_KV_KEY, current);
}

/** Clear a row's forced-unread override (when it is marked read again). */
export async function clearForcedUnread(id: string): Promise<void> {
  const current = await readIdSet(FORCED_UNREAD_KV_KEY);
  if (current.delete(id)) await writeIdSet(FORCED_UNREAD_KV_KEY, current);
}

/** Read every table the derivation needs, once. */
async function readAll(): Promise<DeriveInputs> {
  const [
    messages,
    reactions,
    conversations,
    pollVotes,
    callHistory,
    reminders,
    readPointers,
    dismissedIds,
    forcedUnreadIds,
  ] = await Promise.all([
    db.messages.toArray(),
    db.reactions.toArray(),
    db.conversations.toArray(),
    db.poll_votes.toArray(),
    db.call_history.toArray(),
    db.reminders.toArray(),
    db.read_pointers.toArray(),
    readIdSet(DISMISSED_KV_KEY),
    readIdSet(FORCED_UNREAD_KV_KEY),
  ]);
  return {
    messages,
    reactions,
    conversations,
    pollVotes,
    callHistory,
    reminders,
    readPointers,
    dismissedIds,
    forcedUnreadIds,
  };
}

/**
 * `ActivityLoader` — one-shot read of the derived feed. Matches the
 * loader signature `<ActivityFeedScreen>` owns.
 */
export const loadActivityFeed: ActivityLoader = async (filter, sort) => {
  const self = getCurrentUserAccountId();
  const selfName = getCurrentUserDisplayName();
  const inputs = await readAll();
  const items = deriveActivityItems(self, inputs, selfName);
  return applyFilterSort(items, filter, sort);
};

/**
 * `ActivitySubscriber` — re-projects the derived feed on every Dexie
 * change via `liveQuery`. When a BE push source has been registered
 * (`registerActivityPushSource`) it is preferred and the local
 * derivation is skipped.
 */
export const subscribeActivityFeed: ActivitySubscriber = (
  filter,
  sort,
  callback,
) => {
  if (activityPushSource !== null) {
    return activityPushSource(filter, sort, (items) =>
      callback(applyFilterSort(items, filter, sort)),
    );
  }

  // Read "me" at DERIVE time (not once at subscribe time). The self account id
  // registers from `apps/web` identity bootstrap, which can land AFTER this
  // subscription is created (a reload race / the feed mounts eagerly). Closing
  // over a null `self` would leave the feed PERMANENTLY empty — so re-read it on
  // every projection, and force a re-projection the moment identity resolves.
  const emit = (inputs: DeriveInputs): void => {
    const items = deriveActivityItems(
      getCurrentUserAccountId(),
      inputs,
      getCurrentUserDisplayName(),
    );
    callback(applyFilterSort(items, filter, sort));
  };

  const subscription = liveQuery(() => readAll()).subscribe({
    next: (inputs) => emit(inputs),
    error: () => {
      // Swallow — the view-model keeps the last good state; the loader
      // surfaces hard failures.
    },
  });
  const unsubscribeIdentity = subscribeCurrentUserAccountId(() => {
    void readAll().then(emit);
  });
  return () => {
    subscription.unsubscribe();
    unsubscribeIdentity();
  };
};
